/**
 * Relevo da área da rede (Terrain Tiles/Terrarium) para a cena 3D — Nexora Grid (spike S3).
 *
 * Criado em: 16/10/2026 · Última modificação: 16/10/2026 · Versão: 1.0.0
 *
 * Baixa os tiles de zoom 14 que cobrem a área, decodifica no navegador (cota = R·256 + G + B/256 − 32768) e
 * oferece a cota de qualquer ponto em metros locais. Em produção a fonte é servida pela plataforma.
 */
import type { Rede } from "./rede";

const URL = "https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png";
const ZOOM = 14;

export type Relevo = { cota: (x: number, z: number) => number };

function tileXY(lon: number, lat: number) {
  const n = 2 ** ZOOM;
  const rad = (lat * Math.PI) / 180;
  return { x: ((lon + 180) / 360) * n, y: ((1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2) * n };
}

async function pixels(tx: number, ty: number): Promise<Uint8ClampedArray> {
  const imagem = new Image();
  imagem.crossOrigin = "anonymous";
  imagem.src = URL.replace("{z}", String(ZOOM)).replace("{x}", String(tx)).replace("{y}", String(ty));
  await imagem.decode();
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = 256;
  const ctx = canvas.getContext("2d", { willReadFrequently: true })!;
  ctx.drawImage(imagem, 0, 0);
  return ctx.getImageData(0, 0, 256, 256).data;
}

/** Carrega o relevo que cobre a rede (com margem) e devolve a função de cota em metros locais. */
export async function carregarRelevo(rede: Rede, margem = 0.3): Promise<Relevo> {
  const mPorGrauLat = 110540;
  const mPorGrauLon = mPorGrauLat * Math.cos((rede.origem.lat * Math.PI) / 180);
  const meio = (rede.extensao * (1 + margem)) / 2;
  const canto1 = tileXY(rede.origem.lon - meio / mPorGrauLon, rede.origem.lat + meio / mPorGrauLat);
  const canto2 = tileXY(rede.origem.lon + meio / mPorGrauLon, rede.origem.lat - meio / mPorGrauLat);
  const tiles = new Map<string, Uint8ClampedArray>();
  const pedidos: Promise<void>[] = [];
  for (let tx = Math.floor(canto1.x); tx <= Math.floor(canto2.x); tx++) {
    for (let ty = Math.floor(canto1.y); ty <= Math.floor(canto2.y); ty++) {
      pedidos.push(pixels(tx, ty).then((dados) => { tiles.set(`${tx}/${ty}`, dados); }));
    }
  }
  await Promise.all(pedidos);
  const cota = (x: number, z: number) => {
    const lon = rede.origem.lon + x / mPorGrauLon;
    const lat = rede.origem.lat - z / mPorGrauLat;
    const t = tileXY(lon, lat);
    const dados = tiles.get(`${Math.floor(t.x)}/${Math.floor(t.y)}`);
    if (!dados) return rede.cotaMin;
    const i = (Math.floor((t.y % 1) * 256) * 256 + Math.floor((t.x % 1) * 256)) * 4;
    return dados[i] * 256 + dados[i + 1] + dados[i + 2] / 256 - 32768;
  };
  return { cota };
}
